import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'

export function useCheckins() {
  const { user } = useAuth()
  const [pending, setPending] = useState([])
  const [sent, setSent] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchPending = useCallback(async () => {
    if (!user) {
      setPending([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Fetch pending incoming check-ins (not expired, not yet responded)
      const { data, error: fetchError } = await supabase
        .from('checkin_requests')
        .select(`
          id,
          from_user_id,
          status,
          call_requested,
          call_accepted,
          created_at,
          expires_at,
          sender:users!checkin_requests_from_user_id_fkey (
            id,
            display_name,
            avatar_url,
            status_emoji,
            status_color
          )
        `)
        .eq('to_user_id', user.id)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      setPending(data || [])
    } catch (err) {
      console.error('Error fetching pending check-ins:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [user])

  const fetchSent = useCallback(async () => {
    if (!user) {
      setSent([])
      return
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('checkin_requests')
        .select(`
          id,
          to_user_id,
          status,
          call_requested,
          call_accepted,
          created_at,
          responded_at,
          expires_at,
          recipient:users!checkin_requests_to_user_id_fkey (
            id,
            display_name,
            avatar_url,
            status_emoji,
            status_color
          )
        `)
        .eq('from_user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50)

      if (fetchError) throw fetchError

      setSent(data || [])
    } catch (err) {
      console.error('Error fetching sent check-ins:', err)
    }
  }, [user])

  useEffect(() => {
    Promise.all([fetchPending(), fetchSent()])
  }, [fetchPending, fetchSent])

  // Subscribe to realtime updates for incoming check-ins
  useEffect(() => {
    if (!user) return

    const channel = supabase
      .channel(`checkin-updates-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'checkin_requests',
          filter: `to_user_id=eq.${user.id}`,
        },
        () => {
          fetchPending()
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'checkin_requests',
          filter: `to_user_id=eq.${user.id}`,
        },
        (payload) => {
          setPending(prev => prev.filter(c => c.id !== payload.new.id))
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user, fetchPending])

  const sendCheckin = useCallback(async (toUserId, callRequested = false) => {
    if (!user) throw new Error('Not authenticated')

    // Call the validate-checkin edge function
    const { data: validationResult, error: validationError } = await supabase.functions.invoke(
      'validate-checkin',
      {
        body: { toUserId },
      }
    )

    if (validationError) {
      throw new Error(validationError.message || 'Validation failed')
    }

    if (!validationResult?.valid) {
      throw new Error(validationResult?.reason || 'Check-in not allowed at this time')
    }

    // Create the check-in request
    const expiresAt = new Date()
    expiresAt.setHours(expiresAt.getHours() + 48)

    const { data, error } = await supabase
      .from('checkin_requests')
      .insert({
        from_user_id: user.id,
        to_user_id: toUserId,
        status: 'pending',
        call_requested: callRequested,
        expires_at: expiresAt.toISOString(),
      })
      .select()
      .single()

    if (error) throw error

    // Notify recipient (non-blocking)
    const senderName = user.user_metadata?.display_name || 'Someone'
    supabase.functions.invoke('send-push-notification', {
      body: {
        userId: toUserId,
        title: 'New check-in',
        body: callRequested
          ? `${senderName} is checking in and would love to talk`
          : `${senderName} is checking in with you`,
        data: { requestId: data.id },
      },
    }).catch(() => {})

    await fetchSent()
    return data
  }, [user, fetchSent])

  const respondToCheckin = useCallback(async (checkinId, thumbsUp, callAccepted = null) => {
    if (!user) throw new Error('Not authenticated')

    const updateData = {
      status: thumbsUp ? 'thumbs_up' : 'thumbs_down',
      responded_at: new Date().toISOString(),
    }

    if (callAccepted !== null) {
      updateData.call_accepted = callAccepted
    }

    const { data, error } = await supabase
      .from('checkin_requests')
      .update(updateData)
      .eq('id', checkinId)
      .eq('to_user_id', user.id)
      .select(`
        id,
        from_user_id,
        status,
        call_requested,
        call_accepted,
        sender:users!checkin_requests_from_user_id_fkey (
          id,
          display_name,
          phone_number
        )
      `)
      .single()

    if (error) throw error

    // Notify sender of response (non-blocking)
    const responderName = user.user_metadata?.display_name || 'Someone'
    let notifBody
    if (callAccepted === true) {
      notifBody = `${responderName} accepted your call request — give them a ring!`
    } else if (callAccepted === false) {
      notifBody = `${responderName} responded but declined the call`
    } else {
      notifBody = thumbsUp
        ? `${responderName} is doing okay`
        : `${responderName} responded to your check-in`
    }
    supabase.functions.invoke('send-push-notification', {
      body: {
        userId: data.from_user_id,
        title: 'Check-in response',
        body: notifBody,
        data: { url: '/dashboard' },
      },
    }).catch(() => {})

    setPending(prev => prev.filter(c => c.id !== checkinId))
    return data
  }, [user])

  const getLastCheckinForMember = useCallback(async (memberId) => {
    if (!user) return null

    const { data } = await supabase
      .from('checkin_requests')
      .select('id, created_at, status')
      .eq('from_user_id', user.id)
      .eq('to_user_id', memberId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single()

    return data
  }, [user])

  return {
    pending,
    sent,
    loading,
    error,
    pendingCount: pending.length,
    refetchPending: fetchPending,
    refetchSent: fetchSent,
    sendCheckin,
    respondToCheckin,
    getLastCheckinForMember,
  }
}

export default useCheckins
